import cx from 'classnames'

import Styles from './PlanDetails.module.scss'

import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import ProIcon from 'blaze-ui/icons/20/ProIcon'
import TeamPlanIcon from 'blaze-ui/icons/20/TeamPlanIcon'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import { FeatureList } from './FeatureList'
import { AgencyPlanIcon, Cloud } from 'blaze-ui/icons'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useRootStore } from 'stores/RootStore'
import dayjs from 'dayjs'
import { formatPrice } from 'components/Settings/TieredBilling/utils'
import { DEMO_PATH } from 'paths'
import { getSearchString } from 'utils/urlUtils'
import ArrowRight from 'blaze-ui/icons/20/ArrowRight'
import Marker from 'blaze-ui/icons/20/Marker'
import Star2 from 'blaze-ui/icons/20/Star2'

const ENTERPRISE_IMAGE_URL =
  'https://res.cloudinary.com/almanac/image/upload/c_scale,w_1468/v1772554103/blaze_assets/virtual_marketer/onboarding/kive-wide_eg75gk.jpg'

export const planOrder: any = {
  [PaymentPlanKey.Free]: 0,
  [PaymentPlanKey.BlazeAutopilotStarter]: 1,
  [PaymentPlanKey.VirtualMarketer]: 1,
  [PaymentPlanKey.BlazeAutopilotGrowth]: 2,
  [PaymentPlanKey.BlazeAutopilotEnterprise]: 3,
}

export const iconMap: any = {
  [PaymentPlanKey.Free]: Cloud,
  [PaymentPlanKey.BlazeAutopilotStarter]: TeamPlanIcon,
  [PaymentPlanKey.VirtualMarketer]: Marker,
  [PaymentPlanKey.BlazeAutopilotGrowth]: ProIcon,
  [PaymentPlanKey.BlazeAutopilotEnterprise]: AgencyPlanIcon,
}

export const nameMap: any = {
  [PaymentPlanKey.Free]: 'Free',
  [PaymentPlanKey.BlazeAutopilotStarter]: 'Starter',
  [PaymentPlanKey.VirtualMarketer]: 'Pioneer',
  [PaymentPlanKey.BlazeAutopilotGrowth]: 'Growth',
  [PaymentPlanKey.BlazeAutopilotEnterprise]: 'Enterprise',
}

export const descriptionMap: Partial<Record<PaymentPlanKey, string>> = {
  [PaymentPlanKey.BlazeAutopilotStarter]: 'Everything you need to publish like a pro.',
  [PaymentPlanKey.BlazeAutopilotGrowth]: 'Scale your channels and paid strategy.',
  [PaymentPlanKey.BlazeAutopilotEnterprise]: 'For agencies and marketing teams managing multiple brands.',
}

export const planIconColorMap: Partial<Record<PaymentPlanKey, string>> = {
  [PaymentPlanKey.BlazeAutopilotStarter]: '#FFAE00',
  [PaymentPlanKey.VirtualMarketer]: '#000000',
  [PaymentPlanKey.BlazeAutopilotGrowth]: '#0083E2',
  [PaymentPlanKey.BlazeAutopilotEnterprise]: '#675BFF',
}

const ENTERPRISE_PRICE = 899

export interface CurrentPlan {
  monthlyPrice: number | string
  length?: number
  key: PaymentPlanKey
}

export const PlanDetails = ({
  planKey,
  monthlyPrice,
  creditLimit,
  currentPlan,
  onSelectPlan,
  isTrialing,
  isProcessing,
  buttonTextOverride,
  billingSettingsVariant,
  flexHeight,
}: {
  planKey: PaymentPlanKey
  monthlyPrice?: number
  creditLimit?: number
  currentPlan: CurrentPlan
  onSelectPlan: () => void
  isTrialing: boolean
  isProcessing: boolean
  buttonTextOverride?: string
  billingSettingsVariant?: boolean
  flexHeight?: boolean
}) => {
  const { currentSubscription } = useRootStore().tierUsageStore

  const isEnterprise = planKey === PaymentPlanKey.BlazeAutopilotEnterprise
  if (!isEnterprise && !monthlyPrice && monthlyPrice !== 0) return null

  const price = currentPlan.key === planKey ? currentPlan.monthlyPrice : formatPrice(monthlyPrice)
  const currentPlanKey = currentPlan.key
  const name = nameMap[planKey]

  const PlanIcon = iconMap[planKey]
  const planIconColor = planIconColorMap[planKey]

  const isCanceledOrCancelAt = currentSubscription?.status === 'canceled' || currentSubscription?.cancel_at

  let buttonVariant: ButtonVariant = 'secondary'
  if (currentSubscription?.cancel_at && currentPlanKey === planKey) {
    buttonVariant = 'primary'
  }
  if (isTrialing && currentPlanKey === planKey) {
    buttonVariant = 'secondary'
  }
  const buttonText = () => {
    if (buttonTextOverride) {
      return buttonTextOverride
    }

    if (
      currentSubscription?.status === 'canceled' ||
      (planKey === PaymentPlanKey.Free && currentPlanKey !== PaymentPlanKey.Free)
    ) {
      return 'Select Plan'
    }

    if (currentPlanKey === PaymentPlanKey.VirtualMarketer) {
      return 'Select Plan'
    }

    if (isTrialing) {
      return 'Purchase'
    }

    const currentPlanIndex = planOrder[currentPlanKey]
    const newPlanIndex = planOrder[planKey]

    if (currentPlanIndex === newPlanIndex) {
      if (currentSubscription?.cancel_at || isTrialing) {
        return 'Reactivate'
      }

      if (currentPlanKey === PaymentPlanKey.Free) {
        return 'Current Plan'
      }

      return 'Change Plan Length'
    }

    if (newPlanIndex > currentPlanIndex) {
      if (planKey === PaymentPlanKey.BlazeAutopilotGrowth && currentPlanKey === PaymentPlanKey.BlazeAutopilotStarter)
        return 'Upgrade'
      if (planKey === PaymentPlanKey.BlazeAutopilotGrowth) return 'Change Plan Length'
      if (planKey === PaymentPlanKey.BlazeAutopilotStarter) return 'Select Plan'
      return 'Upgrade'
    }

    return 'Select Plan'
  }

  const currentPlanPillText = () => {
    if (currentSubscription?.cancel_at) {
      const expirationDate = dayjs(currentSubscription?.cancel_at).format('M/D')

      if (isTrialing) {
        return `Expiring ${expirationDate}`
      }

      if (currentSubscription?.status === 'active') {
        return `Cancels ${dayjs(currentSubscription?.cancel_at).format('M/D')}`
      }
    }

    if (isTrialing) {
      return 'On free trial'
    }

    if (currentSubscription?.status === 'canceled') {
      return 'Trial Ended'
    }

    return 'Current Plan'
  }

  const buttonIsDisabled = isProcessing || (currentPlanKey === PaymentPlanKey.Free && planKey === PaymentPlanKey.Free)
  const isFreeAndIsOnFree = planKey === PaymentPlanKey.Free && currentPlanKey === PaymentPlanKey.Free
  const showButton = planKey !== PaymentPlanKey.BlazeAutopilotEnterprise && !isFreeAndIsOnFree

  const isStyledPlan =
    planKey === PaymentPlanKey.BlazeAutopilotStarter || planKey === PaymentPlanKey.BlazeAutopilotGrowth

  const showRecommendedPill =
    planKey === PaymentPlanKey.BlazeAutopilotGrowth && currentPlanKey === PaymentPlanKey.BlazeAutopilotStarter

  if (isEnterprise) {
    return (
      <div
        className={cx(Styles.planDetails, Styles.enterpriseCard, Styles[planKey], {
          [Styles.billingEnterpriseCard]: billingSettingsVariant && !flexHeight,
          [Styles.billingEnterpriseCardFlexHeight]: billingSettingsVariant && flexHeight,
          [Styles.shortEnterpriseContent]: isCanceledOrCancelAt,
        })}
        {...(billingSettingsVariant && { 'data-testid': 'billing-enterprise-card' })}
      >
        <div
          className={cx(Styles.enterpriseImageWrapper, {
            [Styles.billingEnterpriseImageWrapper]: billingSettingsVariant && !flexHeight,
            [Styles.billingEnterpriseImageWrapperFlexHeight]: flexHeight,
          })}
        >
          <img src={ENTERPRISE_IMAGE_URL} alt="" className={Styles.enterpriseImage} />
        </div>
        <div className={cx(Styles.enterpriseContent, { [Styles.billingEnterpriseContent]: billingSettingsVariant })}>
          <div className={Styles.enterpriseHeadingRow}>
            {!flexHeight && <Star2 size={20} color="var(--dark-90)" />}
            {billingSettingsVariant ? (
              <Text variant="primary">Done-for-You Marketing</Text>
            ) : (
              <Heading level={3} color="var(--dark-90)">
                Done-for-You Marketing
              </Heading>
            )}
          </div>
          <Text variant="secondary" color="var(--dark-60)" className={Styles.enterpriseDescription}>
            Work directly with a Blaze expert who manages your campaigns, content, and ads for you. Built for businesses
            that want results without running the platform themselves.
          </Text>
          <div
            className={cx(Styles.enterprisePricingAndButtonWrapper, {
              [Styles.checkoutEnterprisePricingAndButtonWrapper]: billingSettingsVariant,
            })}
          >
            <div className={Styles.enterprisePricing}>
              <Text variant="label" color="var(--dark-60)">
                Starting at
              </Text>
              <span className={Styles.enterprisePriceRow}>
                <Text variant="primary">${formatPrice(ENTERPRISE_PRICE)}</Text>
                <Text variant="placeholder" color="var(--dark-50)">
                  /mo
                </Text>
              </span>
            </div>
            <ButtonLink
              elementType="a"
              target="_blank"
              rel="noopener noreferrer"
              variant="secondary"
              size={billingSettingsVariant ? 'md' : 'lg'}
              fullWidth
              to={`${DEMO_PATH}?${getSearchString({ utm_source: 'product', utm_medium: 'billing' })}`}
              endIcon={billingSettingsVariant || !!flexHeight ? undefined : ArrowRight}
            >
              Schedule a Consultation
            </ButtonLink>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cx(Styles.planDetails, Styles[planKey])}>
      {currentPlanKey === planKey && (
        <div className={cx(Styles.currentPlanPill, Styles[planKey])}>{currentPlanPillText()}</div>
      )}
      {showRecommendedPill && (
        <div className={cx(Styles.currentPlanPill, Styles.blaze_autopilot_growth)}>Recommended</div>
      )}
      <div className={cx(Styles.header, Styles[planKey])}>
        <div className={Styles.left}>
          <div className={Styles.nameWithIcon}>
            {PlanIcon && <PlanIcon color={planIconColor} />}
            <Heading level={4} color={isStyledPlan ? 'var(--dark-90)' : 'var(--light-100)'}>
              {name}
            </Heading>
          </div>
          <Heading level={4} color={isStyledPlan ? 'var(--dark-90)' : 'var(--light-100)'}>
            {`$${price}/mo`}
          </Heading>
        </div>
      </div>
      {planKey !== PaymentPlanKey.Free && (
        <div className={Styles.priceAndCta}>
          <Text variant="secondary" color="var(--dark-80)">
            {descriptionMap[planKey]}
          </Text>
          {showButton && (
            <Button size="lg" variant={buttonVariant} fullWidth onPress={onSelectPlan} isDisabled={buttonIsDisabled}>
              {buttonText()}
            </Button>
          )}
        </div>
      )}
      <div className={cx(Styles.featureListWrapper, Styles[planKey])}>
        <FeatureList plan={planKey} currentPlan={currentPlan.key} creditLimit={creditLimit} />
      </div>
    </div>
  )
}
